/**
 * LandingProductEditor — 新建 / 编辑投放产品（ADM-LP-S001）
 *
 * 字段对齐 AE §1.1。logo 上传不在本期（S001 plan NOT-in-scope）。
 * 写接口豁免 operator 门槛（viewer+ 可写），本弹窗不按角色藏按钮。
 */

import {
  type CreateLandingProductRequest,
  CreateLandingProductRequestSchema,
  LANDING_THEME_IDENTITY_KEYS,
  LANDING_THEME_NEUTRAL_KEYS,
  LEGAL_PAGE_PATHS,
  type LandingProductDto,
  type LandingTheme,
  type UpdateLandingProductRequest,
  isLegalUrlPathMismatch,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { useApps } from '~/data/queries';
import { landingAssetsApi } from '~/hooks/use-landing-assets';
import { landingProductsApi } from '~/hooks/use-landing-products';
import { Icons } from '~/lib/icons';
import { LANDING_THEME_PRESETS, checkThemeContrast } from '~/pages/landing/landing-theme-presets';
import { Dialog, DialogBody } from '~/ui/Dialog';
import { ImageUploadField } from '~/ui/ImageUploadField';
import { Select } from '~/ui/Select';
import { Switch } from '~/ui/Switch';
import { toast } from '~/ui/Toast';
import { LandingFormRow as FormRow, LANDING_FORM_DIALOG_WIDTH } from './landing-form-dialog-layout';

const BASE_URL_OPTIONS = [
  { value: 'https://lp.novellea.app', label: 'https://lp.novellea.app · prod' },
  { value: 'https://lp-dev.novellea.app', label: 'https://lp-dev.novellea.app · dev / staging' },
] as const;

const EMPTY_THEME: LandingTheme = {
  bg: '',
  primary: '',
  primary_deep: '',
  primary_dark: '',
  primary_soft: '',
};

interface FormState {
  name: string;
  brand: string;
  /** logo 的 R2 key（空串 = 未配）。承载形态是 { r2_key }，见 shared LandingLogoSchema */
  logo_key: string;
  app_ids: string[];
  onelink_base_url: string;
  legal_privacy_url: string;
  legal_terms_url: string;
  /** 客服反馈邮箱（空串 = 未配，投放页不渲染客服悬浮按钮）*/
  support_email: string;
  theme: LandingTheme;
  base_url: string;
  tracking_enabled: boolean;
}

function fromDto(dto: LandingProductDto): FormState {
  return {
    name: dto.name,
    brand: dto.brand,
    logo_key: dto.logo?.r2_key ?? '',
    app_ids: [...dto.app_ids],
    onelink_base_url: dto.onelink_base_url ?? '',
    legal_privacy_url: dto.legal_privacy_url,
    legal_terms_url: dto.legal_terms_url,
    support_email: dto.support_email ?? '',
    theme: { ...dto.theme },
    base_url: dto.base_url,
    tracking_enabled: dto.tracking_enabled,
  };
}

function emptyForm(): FormState {
  return {
    name: '',
    brand: '',
    logo_key: '',
    app_ids: [],
    onelink_base_url: '',
    legal_privacy_url: '',
    legal_terms_url: '',
    support_email: '',
    theme: { ...EMPTY_THEME },
    base_url: 'https://lp-dev.novellea.app',
    tracking_enabled: true,
  };
}

function normalizeTheme(theme: LandingTheme): LandingTheme {
  const next: LandingTheme = { ...theme };
  for (const key of LANDING_THEME_NEUTRAL_KEYS) {
    if (!next[key]) delete next[key];
  }
  return next;
}

function toCreateBody(form: FormState): CreateLandingProductRequest {
  return {
    name: form.name.trim(),
    brand: form.brand.trim(),
    // 未配 logo 时下发 null 而不是省略键：产品可以从「有 logo」改回「没有」，
    // 省略键在 PATCH 语义下等于「不改」，那样就摘不掉了
    logo: form.logo_key ? { r2_key: form.logo_key } : null,
    app_ids: form.app_ids,
    onelink_base_url: form.onelink_base_url.trim() || null,
    legal_privacy_url: form.legal_privacy_url.trim(),
    legal_terms_url: form.legal_terms_url.trim(),
    // 空串 = 未配 → 下发 null。留空串会被后端 Zod 的 email 校验拒
    support_email: form.support_email.trim() || null,
    theme: normalizeTheme(form.theme),
    base_url: form.base_url,
    tracking_enabled: form.tracking_enabled,
  };
}

function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

function sameTheme(a: LandingTheme, b: LandingTheme): boolean {
  return JSON.stringify(normalizeTheme(a)) === JSON.stringify(normalizeTheme(b));
}

/** PATCH 只发相对打开弹窗时快照有变化的字段，避免整包覆盖并发改动 */
export function toUpdatePatch(
  form: FormState,
  original: LandingProductDto,
  listedIds: Set<string> | null,
): UpdateLandingProductRequest | null {
  const next = toCreateBody(form);
  if (next.app_ids.length !== 1) throw new Error('请选择唯一关联 App 后保存');
  const patch: UpdateLandingProductRequest = {};
  if (next.name !== original.name) patch.name = next.name;
  if (next.brand !== original.brand) patch.brand = next.brand;
  if (!sameIdSet(next.app_ids, original.app_ids)) {
    // 替换关联必须是运营明确选择的唯一有效 App，不能过滤旧成员后静默改变目标。
    if (!listedIds) throw new Error('App 列表加载失败，无法改关联');
    if (!listedIds.has(next.app_ids[0] ?? '')) throw new Error('请选择 1 个有效 App');
    patch.app_ids = next.app_ids;
  }
  if (next.onelink_base_url !== original.onelink_base_url)
    patch.onelink_base_url = next.onelink_base_url;
  if (next.legal_privacy_url !== original.legal_privacy_url)
    patch.legal_privacy_url = next.legal_privacy_url;
  if (next.legal_terms_url !== original.legal_terms_url)
    patch.legal_terms_url = next.legal_terms_url;
  // 比归一后的值：'' 与 null 都表示未配，不该判成有改动
  if ((next.support_email ?? null) !== (original.support_email ?? null))
    patch.support_email = next.support_email;
  if (!sameTheme(next.theme, original.theme)) patch.theme = next.theme;
  // logo 比 r2_key，不比对象：两次渲染出的 { r2_key } 是不同对象，直接 !== 会每次都判成有改动
  if ((next.logo?.r2_key ?? null) !== (original.logo?.r2_key ?? null)) patch.logo = next.logo;
  if (next.base_url !== original.base_url) patch.base_url = next.base_url;
  if (next.tracking_enabled !== original.tracking_enabled)
    patch.tracking_enabled = next.tracking_enabled;
  return Object.keys(patch).length > 0 ? patch : null;
}

function isHex6(value: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(value);
}

/**
 * OneLink **template** 形态：域名 + 模板 ID 两段，后面不得再有路径。
 * custom link（带更多段或 query）会被生成器直接拒，故这里提前拦（AE §1.1）。
 */
const ONELINK_TEMPLATE_RE = /^https:\/\/[A-Za-z0-9.-]+\/[A-Za-z0-9_-]+$/;

function isHttpsUrl(value: string): boolean {
  return /^https:\/\/\S+$/.test(value.trim());
}

/**
 * 单个主题色的录入卡——设计稿 dialogs-landing.jsx §投放产品 的色卡形态：
 * 一个 16px 色片 + 右侧「键名 / 窄 hex 输入」两行，整组 flex-wrap 排在一行里，
 * 而不是每个键各占一个表单行。
 */
function ColorCard({
  value,
  onChange,
  placeholder,
  fieldKey,
  muted,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  /** 卡面上的键名。设计稿把 key 显示在窄 input 上方，而不是另起一列标签 */
  fieldKey: string;
  /** 中性色的键名用弱化色——它们可空，视觉权重低于身份色 */
  muted?: boolean;
}) {
  const filled = isHex6(value);
  return (
    <div
      className="hstack"
      style={{
        gap: 6,
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '4px 6px',
      }}
    >
      {/*
       * 取色块恒可点：设计稿这里是只读的预览方块，但空态下那等于「没有取色器」——
       * 运营必须先手打 #RRGGBB 才拿得到调色盘。故升级成原生取色控件（外观由
       * .swatch-input 抹平），未填时用 .swatch-input-empty 画成稿上的 dashed 空框。
       * value 在未填时给个兜底色，因为 <input type="color"> 不接受空字符串。
       */}
      <input
        type="color"
        className={`swatch-input${filled ? '' : ' swatch-input-empty'}`}
        style={{ width: 16, height: 16 }}
        value={filled ? value : '#FFFFFF'}
        aria-label={`${fieldKey} 取色`}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
      />
      <div>
        <div
          className="mono"
          style={{ fontSize: 'var(--text-2xs)', color: muted ? 'var(--fg-muted)' : undefined }}
        >
          {fieldKey}
        </div>
        <input
          className="input mono"
          value={value}
          placeholder={placeholder}
          aria-label={fieldKey}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: 92,
            height: 'var(--input-h)',
            fontSize: 'var(--text-sm)',
            padding: '0 6px',
          }}
        />
      </div>
    </div>
  );
}

export function LandingProductEditor({
  product,
  onClose,
  onOpenAudit,
  onRenderingChanged,
}: {
  product: LandingProductDto | null;
  onClose: () => void;
  onOpenAudit?: () => void;
  onRenderingChanged?: (change: NonNullable<LandingProductDto['html_change']>) => void;
}) {
  const isEdit = product !== null;
  const queryClient = useQueryClient();
  const { data: apps, isLoading: appsLoading, isError: appsError } = useApps();
  const [form, setForm] = useState<FormState>(() => (product ? fromDto(product) : emptyForm()));
  const [clientError, setClientError] = useState<string | null>(null);

  const patch = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // 提交前的本地预校验。真正的拒绝仍由后端 Zod 做，这里只为把失败原因提前摊在 footer 上，
  // 省掉「点了创建才知道哪一项不合格」那一轮。
  const webOnly =
    form.app_ids.length > 0 &&
    form.app_ids.every(
      (id) => String(apps.find((app) => app.id === id)?.platform).toLowerCase() === 'web',
    );
  const onelinkOk = webOnly || ONELINK_TEMPLATE_RE.test(form.onelink_base_url.trim());
  const identityOk = LANDING_THEME_IDENTITY_KEYS.every((k) => isHex6(form.theme[k]));
  const urlsOk = isHttpsUrl(form.legal_privacy_url) && isHttpsUrl(form.legal_terms_url);
  // dev-docs 60 §8：法务页路径固定。只提示不阻断 —— AE 契约对这两个字段只要求 https URL。
  const legalPathWarnings = [
    isLegalUrlPathMismatch(form.legal_privacy_url, LEGAL_PAGE_PATHS.privacy_policy)
      ? `隐私政策应为 …${LEGAL_PAGE_PATHS.privacy_policy}`
      : null,
    isLegalUrlPathMismatch(form.legal_terms_url, LEGAL_PAGE_PATHS.user_agreement)
      ? `服务条款应为 …${LEGAL_PAGE_PATHS.user_agreement}`
      : null,
  ].filter((w): w is string => w !== null);
  /** 中性色是否有自定义值 —— 决定「清空」是否可用，以及旁边那句状态文案 */
  const neutralDirty = LANDING_THEME_NEUTRAL_KEYS.some((key) => !!form.theme[key]);

  /** 只报告不阻断：生成器不校验对比度，运营有正当理由偏离时不该被拦 */
  const contrastIssues = checkThemeContrast(form.theme);

  const appSelectionInvalid = form.app_ids.length !== 1;
  const blockReason = appSelectionInvalid
    ? '请选择唯一关联 App 后保存'
    : !webOnly && !onelinkOk
      ? 'OneLink 必须是 template 形态（域名 + 模板 ID 两段），custom link 会被生成器拒绝'
      : !identityOk
        ? '身份色 5 键必填 —— 缺任一生成器直接失败'
        : !urlsOk
          ? '法务链接需为 https URL'
          : null;

  const listedIds = useMemo(() => new Set(apps.map((a) => a.id)), [apps]);
  const orphanAppIds = useMemo(
    () => form.app_ids.filter((id) => !listedIds.has(id)),
    [form.app_ids, listedIds],
  );
  const appsFirstLoading = appsLoading && apps.length === 0;
  const appsListBlocked = appsError && apps.length === 0;
  const listedForPatch = apps.length > 0 || (!appsLoading && !appsError) ? listedIds : null;

  const save = useMutation({
    mutationFn: async () => {
      if (appSelectionInvalid) throw new Error('请选择唯一关联 App 后保存');
      // Web-only 不适用 OneLink；先归一再校验与生成 PATCH，残留的无效草稿不能阻止保存。
      const normalizedForm = webOnly ? { ...form, onelink_base_url: '' } : form;
      const body = toCreateBody(normalizedForm);
      const parsed = CreateLandingProductRequestSchema.safeParse(body);
      if (!parsed.success) {
        const first = parsed.error.issues[0];
        throw new Error(
          first ? `${first.path.join('.') || '表单'}：${first.message}` : '表单校验失败',
        );
      }
      if (isEdit && product) {
        const update = toUpdatePatch(normalizedForm, product, listedForPatch);
        if (!update) throw new Error('没有改动');
        return landingProductsApi.update(product.id, update);
      }
      return landingProductsApi.create(parsed.data);
    },
    onSuccess: async (dto) => {
      toast(isEdit ? `已保存 ${dto.name}` : `已创建 ${dto.name}`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['landing-products'] }),
        // 产品渲染配置变更会使关联任务失效，返回列表必须重新取状态。
        queryClient.invalidateQueries({ queryKey: ['landing-pages'] }),
        queryClient.invalidateQueries({ queryKey: ['landing-projects'] }),
      ]);
      onClose();
      if (dto.html_change?.html_inputs_changed) onRenderingChanged?.(dto.html_change);
    },
    onError: (err) => {
      const msg =
        err instanceof AdminApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : '保存失败';
      setClientError(msg);
      toast(msg, { tone: 'neg', icon: 'IconAlert' });
    },
  });

  return (
    <Dialog
      title={isEdit ? `编辑产品 · ${product.name}` : '新建投放产品'}
      subtitle={
        isEdit ? '管理投放产品的渲染身份及关联应用' : '选择一个 App，配置该投放产品的渲染身份'
      }
      icon="IconLink"
      width={LANDING_FORM_DIALOG_WIDTH}
      onClose={onClose}
      footer={
        <>
          {/* 稿上 footer 左侧常驻一行原因说明：不合格时指出卡在哪一项，合格时讲产品不可删 */}
          <div
            style={{
              flex: 1,
              fontSize: 'var(--text-xs)',
              color: blockReason ? 'var(--warn)' : 'var(--fg-muted)',
            }}
          >
            {blockReason ?? '本期无删除端点 · 产品不可删除'}
          </div>
          {isEdit && onOpenAudit && (
            <button type="button" className="btn btn-ghost" onClick={onOpenAudit}>
              <Icons.IconHistory size={13} />
              操作记录
            </button>
          )}
          <button type="button" className="btn" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={
              save.isPending ||
              appSelectionInvalid ||
              (!isEdit && (appsFirstLoading || appsListBlocked))
            }
            onClick={() => {
              if (appSelectionInvalid) return;
              setClientError(null);
              save.mutate();
            }}
          >
            {save.isPending ? '保存中…' : isEdit ? '保存' : '创建产品'}
          </button>
        </>
      }
    >
      <DialogBody>
        <FormRow label="产品运营名" required hint="列表展示用">
          <input
            className="input"
            value={form.name}
            onChange={(e) => patch('name', e.target.value)}
            maxLength={128}
          />
        </FormRow>
        <FormRow label="品牌名 brand" required hint="页面标题 / 页脚 / 顶栏展示">
          <input
            className="input"
            value={form.brand}
            onChange={(e) => patch('brand', e.target.value)}
            maxLength={128}
          />
        </FormRow>
        <FormRow
          label="关联 App"
          required
          hint="请选择一个 App，点击其他 App 可切换；同一 App 可关联多个投放产品"
        >
          {form.app_ids.length > 1 && (
            <span role="alert" className="chip chip-warn">
              当前产品关联多个 App，请明确选择唯一 App 后保存；原关联尚未修改。
            </span>
          )}
          {appsFirstLoading ? (
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
              加载 App 列表…
            </span>
          ) : appsListBlocked ? (
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--neg)' }}>
              App 列表加载失败，无法选择关联
            </span>
          ) : apps.length === 0 && orphanAppIds.length === 0 ? (
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>暂无 App</span>
          ) : (
            <div className="vstack" style={{ gap: 6 }}>
              {appsError && (
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--neg)' }}>
                  App 列表刷新失败，以下为缓存；替换关联后仍由服务端校验有效性
                </span>
              )}
              {/* 设计稿用按钮芯片点选，不是勾选框：选中态 = btn-accent */}
              <div className="hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
                {apps.map((app) => {
                  const on = form.app_ids.includes(app.id);
                  return (
                    <button
                      key={app.id}
                      type="button"
                      aria-pressed={on}
                      className={`btn btn-sm${on ? ' btn-accent' : ''}`}
                      onClick={() => patch('app_ids', [app.id])}
                    >
                      {app.name}
                      <span
                        className="mono"
                        style={{ fontSize: 'var(--text-2xs)', marginLeft: 4, opacity: 0.7 }}
                      >
                        {app.platform}
                        {app.status === 'paused' ? ' · 已停用' : ''}
                      </span>
                    </button>
                  );
                })}
                {/*
                 * 孤儿关联：app_ids 里存在但 App 列表查不到的 id。稿上没有这个态
                 * （稿的数据恒一致），旧关联仍须完整展示；运营选择有效 App 后整组替换。
                 */}
                {orphanAppIds.map((id) => (
                  <button
                    key={id}
                    type="button"
                    aria-pressed
                    className="btn btn-sm btn-accent"
                    disabled
                    title="该 App 已不在列表中，请选择上方一个有效 App 替换关联"
                  >
                    未知 App
                    <span
                      className="mono"
                      style={{ fontSize: 'var(--text-2xs)', marginLeft: 4, opacity: 0.7 }}
                    >
                      {id.slice(0, 8)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </FormRow>
        <FormRow label="logo" hint="启用顶部下载条的任务要求产品已配 logo">
          <ImageUploadField
            label="上传 logo"
            hint={
              <>
                顶栏品牌标识 · 开启<span className="mono"> show_topbar </span>的任务必须有它
              </>
            }
            width={88}
            height={88}
            value={form.logo_key}
            disabled={save.isPending}
            upload={async (file) => (await landingAssetsApi.upload(file, 'logo')).r2_key}
            resolvePreview={landingAssetsApi.previewUrl}
            onChange={(next) => patch('logo_key', next)}
          />
        </FormRow>
        <FormRow
          label="OneLink template"
          required={!webOnly}
          hint={
            webOnly
              ? 'Web-only 产品无需 OneLink，投放计划直接生成阅读链接'
              : 'AppsFlyer OneLink template（域名 + 模板 ID 两段）· media_source 进 pid'
          }
        >
          {/*
           * 稿上这里挂的是 `input-err` class，但设计系统的 tokens.css 里并没有该类
           * （稿自身的悬空引用）。不新造类，直接用既有 --neg token 表达错误边框。
           */}
          <input
            className="input mono"
            value={form.onelink_base_url}
            disabled={webOnly}
            placeholder={webOnly ? '不适用（保存为 NULL）' : 'https://brand.onelink.me/AbCd'}
            style={form.onelink_base_url && !onelinkOk ? { borderColor: 'var(--neg)' } : undefined}
            onChange={(e) => patch('onelink_base_url', e.target.value)}
          />
          {form.onelink_base_url && !onelinkOk && (
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--warn)', marginTop: 4 }}>
              非 template 形态 → <span className="mono">VALIDATION_FAILED</span>
            </div>
          )}
        </FormRow>
        <FormRow
          label="投放页域 base_url"
          required
          hint="取值 = 通用约定「环境与域名」的投放 Web 页域"
        >
          <Select
            value={form.base_url}
            onChange={(e) => patch('base_url', e.target.value)}
            style={{ maxWidth: 320 }}
          >
            {BASE_URL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow
          label="法务链接"
          required
          hint={`页脚隐私 / 条款。路径固定为 ${LEGAL_PAGE_PATHS.privacy_policy} 与 ${LEGAL_PAGE_PATHS.user_agreement}（品牌域名根下），不要另造路径`}
        >
          <div className="vstack" style={{ gap: 6 }}>
            <input
              className="input mono"
              value={form.legal_privacy_url}
              // placeholder 直接给出固定路径：旧的 `https://…/privacy` 本身就在引导运营填出 §8 点名的那类自造路径
              placeholder={`https://<品牌域名>${LEGAL_PAGE_PATHS.privacy_policy}`}
              aria-label="隐私政策"
              onChange={(e) => patch('legal_privacy_url', e.target.value)}
            />
            <input
              className="input mono"
              value={form.legal_terms_url}
              placeholder={`https://<品牌域名>${LEGAL_PAGE_PATHS.user_agreement}`}
              aria-label="服务条款"
              onChange={(e) => patch('legal_terms_url', e.target.value)}
            />
            {legalPathWarnings.length > 0 && (
              <div
                className="card-flat"
                role="note"
                style={{
                  padding: '6px 10px',
                  fontSize: 'var(--text-xs)',
                  background: 'var(--warn-soft)',
                }}
              >
                {legalPathWarnings.join('；')} —— 法务页只在固定路径上存在，自造路径线上会
                404（不阻断保存）。
              </div>
            )}
          </div>
        </FormRow>
        <FormRow
          label="客服反馈邮箱"
          hint="可选 · 配了才在投放页右下角出现客服按钮，点击开邮件；留空则不显示"
        >
          <input
            className="input mono"
            type="email"
            value={form.support_email}
            placeholder="support@example.com"
            onChange={(e) => patch('support_email', e.target.value)}
          />
        </FormRow>
        <FormRow
          label="埋点 tracking_enabled"
          hint="true 时生成注入 tracking_endpoint = base_url 同域 /collect；false 时整体关闭埋点"
        >
          <div className="hstack" style={{ gap: 10, alignItems: 'center' }}>
            <Switch
              checked={form.tracking_enabled}
              onChange={(next) => patch('tracking_enabled', next)}
            />
            {/* 把最终生效的 endpoint 摊开给运营看——光一个开关看不出它究竟往哪打点 */}
            <span className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
              {form.tracking_enabled ? `${form.base_url}/collect` : '不注入'}
            </span>
          </div>
        </FormRow>
        <FormRow
          label="主题色 · 身份色"
          required
          hint="5 键必填 —— 缺任一生成器直接失败，保存时预校验"
        >
          <div className="vstack" style={{ gap: 8 }}>
            {/*
             * 预设：一次填满 5 键。生成器只校验键齐全、不校验深浅，把 5 个键填成同一个值
             * 照样能存能生成，页面发出去才发现正文读不了 —— 预设是那条路的短路。
             * 只覆盖身份色，中性色不动（它们留空走生成器 defaults）。
             */}
            <div className="hstack" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>预设</span>
              {LANDING_THEME_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className="btn btn-sm"
                  title={`${preset.hint} · 点击填满 5 个身份色`}
                  onClick={() => patch('theme', { ...form.theme, ...preset.theme })}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 'var(--radius-sm)',
                      background: preset.theme.primary,
                      marginRight: 4,
                    }}
                  />
                  {preset.name}
                </button>
              ))}
            </div>
            <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
              {LANDING_THEME_IDENTITY_KEYS.map((key) => (
                <ColorCard
                  key={key}
                  fieldKey={key}
                  value={form.theme[key]}
                  placeholder="#RRGGBB"
                  onChange={(next) => patch('theme', { ...form.theme, [key]: next })}
                />
              ))}
            </div>
            {contrastIssues.length > 0 && (
              <div
                className="card-flat"
                style={{ padding: '8px 10px', borderColor: 'var(--warn)' }}
              >
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--warn)', fontWeight: 500 }}>
                  <Icons.IconAlert size={10} /> 对比度不足（不阻断保存，生成器也不校验这个）
                </div>
                {contrastIssues.map((issue) => (
                  <div
                    key={issue.label}
                    style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', marginTop: 3 }}
                  >
                    {issue.label}：<span className="mono">{issue.ratio.toFixed(2)}:1</span>
                    （需 ≥{issue.need}）
                  </div>
                ))}
              </div>
            )}
          </div>
        </FormRow>
        <FormRow label="主题色 · 中性色" hint="可选 · 仅产品需偏离通用值时配置">
          <div className="vstack" style={{ gap: 8 }}>
            {/*
             * 这里不设「中性色预设」：默认值的事实源在生成器的 config/defaults.json，
             * admin 再存一份必然漂移（同 copy.keys 那条教训 —— 生成器改了，admin 这份不会跟着变，
             * 而运营看不出显示的默认值已经不是页面上那个）。
             * 需要的是**清空**：留空即走生成器默认，而 UI 上原本没有恢复入口 —— 填错了只能逐个删。
             */}
            <div className="hstack" style={{ gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-sm"
                disabled={!neutralDirty}
                title="清空 5 个中性色 —— 留空即由生成器的通用值渲染"
                onClick={() => {
                  const next = { ...form.theme };
                  for (const key of LANDING_THEME_NEUTRAL_KEYS) delete next[key];
                  patch('theme', next);
                }}
              >
                <Icons.IconRefresh size={11} />
                清空 · 走系统默认
              </button>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
                {neutralDirty ? '当前有自定义值，会覆盖通用值' : '全部留空 · 由生成器通用值渲染'}
              </span>
            </div>
            <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
              {LANDING_THEME_NEUTRAL_KEYS.map((key) => (
                <ColorCard
                  key={key}
                  fieldKey={key}
                  muted
                  value={form.theme[key] ?? ''}
                  placeholder="继承通用值"
                  onChange={(next) => patch('theme', { ...form.theme, [key]: next })}
                />
              ))}
            </div>
          </div>
        </FormRow>
        {clientError && (
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--neg)', marginTop: 8 }}>
            {clientError}
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}
